import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	ViewChild,
	OnInit,
	HostListener,
} from '@angular/core';
import { DialogPosition, MatDialog } from '@angular/material/dialog';
import {
	MatDrawerMode,
	MatSidenavContainer,
	MatSidenav,
	MatSidenavContent,
} from '@angular/material/sidenav';
import { Subscription } from 'rxjs';
import { AssignedRole } from 'src/app/models/data/assignedRole';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import { AuthService } from 'src/app/services/auth.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { NavigationHistoryService } from '../services/navigation-history.service';
import { UserProfileService } from '../services/user-profile.service';
import { DeviceGenerateCodeDialogComponent } from 'src/app/components/device-generate-code-dialog/device-generate-code-dialog.component';
import {
	CHANGE_ROLE_LOADING_KEY,
	NavProfileInformationComponent,
} from './nav-profile-information.component';
import { LoadingIndicatorService } from '../services/loading-indicator.service';
import { NavStateService } from './nav-state.service';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MOBILE_BREAKPOINT } from '../app.constants';
import { BannerMessageComponent } from '../components/banner-message/banner-message.component';
import { MatIcon } from '@angular/material/icon';
import { LoadingIndicatorComponent } from '../components/loading-indicator/loading-indicator.component';
import { MatDivider, MatNavList, MatListItem } from '@angular/material/list';
import { RouterLink, RouterOutlet } from '@angular/router';
import { MatButton } from '@angular/material/button';

export type MenuItem = {
	title: string;
	link: string;
	icon: string;
	type: MenuItemType;
	subtabIndex?: number;
};

export enum MenuItemType {
	home,
	companies,
	users,
	properties,
	clients,
	devices,
	roles,
	reports,
	deviceAccess,
	billing,
	learning,
}

export const NAV_ITEM_AUTOMATION_IDS: Record<MenuItemType, string> = {
	[MenuItemType.home]: 'Home',
	[MenuItemType.companies]: 'Companies',
	[MenuItemType.users]: 'Users',
	[MenuItemType.properties]: 'Properties',
	[MenuItemType.clients]: 'Clients',
	[MenuItemType.devices]: 'Devices',
	[MenuItemType.roles]: 'Roles',
	[MenuItemType.reports]: 'Reports',
	[MenuItemType.deviceAccess]: 'Device Access',
	[MenuItemType.billing]: 'Billing',
	[MenuItemType.learning]: 'Learning',
};

@Component({
	selector: 'app-nav',
	templateUrl: './nav.component.html',
	styleUrls: ['./nav.component.scss', '../ui/common/main.scss'],
	imports: [
		BannerMessageComponent,
		MatSidenavContainer,
		MatSidenav,
		MatIcon,
		LoadingIndicatorComponent,
		MatDivider,
		MatNavList,
		MatListItem,
		RouterLink,
		MatButton,
		MatSidenavContent,
		RouterOutlet,
	],
})
export class NavComponent implements AfterViewInit, OnDestroy, OnInit {
	@ViewChild('userNameRef', { static: false }) public userNameRef!: ElementRef;

	readonly navItemAutomationIds = NAV_ITEM_AUTOMATION_IDS;

	menuItems: MenuItem[] = [];
	userProfile?: UserProfile;
	profileSubscription?: Subscription;
	navigationSubscription?: Subscription;
	scSubscription?: Subscription;
	breakpointSub?: Subscription;

	lastSelectedNavItem?: MenuItemType;
	loadingChangeRoleKey = CHANGE_ROLE_LOADING_KEY;

	// Initial responsive properties here to avoid flickering of nav
	isMobile = window.innerWidth <= MOBILE_BREAKPOINT; // 992px is the breakpoint for mobile
	sidenavOpened = this.isMobile ? !this.navState.hasShownMobileSidenav : true;
	prevIsMobile = this.isMobile;
	isNavigating = false;

	openMenuText = $localize`:@@navComponent-openMenu:Open menu`;
	closeMenuText = $localize`:@@navComponent-closeMenu:Close menu`;
	generateCodeIconText = $localize`:@@deviceGenerateOneDayCode-generateCodeAltIconText:Add Generated Code`;

	sideNavMode: MatDrawerMode = 'side';

	private checkScreenSize(isMobile: boolean): void {
		const wasMobile = this.isMobile;
		this.isMobile = isMobile;
		// Prevent closing the sidenav on resize if the user is already on mobile
		if (this.isMobile && !this.isNavigating && this.isMobile && !wasMobile) {
			this.sidenavOpened = false;
		} else if (!this.isMobile) {
			this.sidenavOpened = true;
		}
	}

	get sidenavMode(): MatDrawerMode {
		return this.isMobile ? 'over' : 'side';
	}

	get loadingChangeRole(): boolean {
		return this.loadingService.isLoading(this.loadingChangeRoleKey);
	}

	isSentrilock: boolean | undefined = false;
	activeCompanyCountry: string | undefined = '';
	canIssueFlexCodes = false;

	get userName(): string {
		const user = this.userProfile?.user;
		return `${user?.firstName ?? ''} ${user?.lastName ?? ''}`;
	}

	get userNameAbbreviation(): string {
		const user = this.userProfile?.user;
		return `${user?.firstName.substring(0, 1) ?? ''}${user?.lastName.substring(0, 1) ?? ''}`;
	}

	get assignedRole(): AssignedRole | undefined {
		const activeEntityRelationshipId =
			this.userProfile?.activeRoleDetails?.entityRelationship?.id ?? '';
		if (activeEntityRelationshipId === '') {
			return undefined;
		}
		return this.userProfile?.availableAssignedRoles.find(
			(it) => it.entityRelationshipId === activeEntityRelationshipId
		);
	}

	constructor(
		private readonly authService: AuthService,
		private readonly dialog: MatDialog,
		private readonly scService: SystemCapabilityService,
		private readonly navHistory: NavigationHistoryService,
		private readonly profileService: UserProfileService,
		private readonly navState: NavStateService,
		readonly loadingService: LoadingIndicatorService,
		readonly breakpointObserver: BreakpointObserver
	) {}

	private refreshCapabilityDependentState(): void {
		this.canIssueFlexCodes = this.scService.has(
			SystemCapabilityEnum.issueFlexCodes
		);
		this.updateMenuItems();
	}

	// tslint:disable-next-line: typedef
	updateMenuItems() {
		this.menuItems = [
			// tslint:disable: max-line-length
			{
				type: MenuItemType.home,
				title: $localize`:@@navComponent-home:Home`,
				link: '/',
				icon: 'sl-nav-home',
			},
			{
				type: MenuItemType.companies,
				title: $localize`:@@navComponent-companies:Companies`,
				link: '/company',
				icon: 'sl-nav-work',
				subtabIndex: 0,
			},
			{
				type: MenuItemType.users,
				title: $localize`:@@navComponent-users:Users`,
				link: '/users',
				icon: 'sl-nav-profile',
				subtabIndex: 0,
			},
			{
				type: MenuItemType.properties,
				title: $localize`:@@navComponent-properties:Properties`,
				link: '/properties',
				icon: 'sl-nav-location',
			},
			{
				type: MenuItemType.devices,
				title: $localize`:@@navComponent-devices:Devices`,
				link: '/devices',
				icon: 'sl-nav-password',
				subtabIndex: 0,
			},
			{
				type: MenuItemType.roles,
				title: $localize`:@@navComponent-roles:Roles`,
				link: '/roles',
				icon: 'sl-nav-setting',
			},
		];

		// Check if company should have access to Clients nav
		// This is determined on the backend by checking if:
		// 1. Company is a Care company (companyType === 'care'), OR
		// 2. Company has access to devices associated with clients
		if (this.userProfile?.activeRoleDetails?.company?.hasClientAccess) {
			// Insert Clients after Users
			const usersIndex = this.menuItems.findIndex(
				(item) => item.type === MenuItemType.users
			);
			this.menuItems.splice(usersIndex + 1, 0, {
				type: MenuItemType.clients,
				title: $localize`:@@navComponent-clients:Clients`,
				link: '/clients',
				icon: 'sl-nav-client',
			});
		}

		if (this.scService.has(SystemCapabilityEnum.runReports)) {
			this.menuItems.push({
				type: MenuItemType.reports,
				title: $localize`:@@navComponent-reports:Reports`,
				link: '/reports',
				icon: 'sl-nav-chart',
			});
		}

		if (this.scService.has(SystemCapabilityEnum.billingManagement)) {
			this.menuItems.push({
				type: MenuItemType.billing,
				title: $localize`:@@navComponent-billing:Billing`,
				link: '/billing',
				icon: 'sl-nav-billing',
			});
		}

		if (
			this.isSentrilock ||
			(this.activeCompanyCountry !== '' && this.activeCompanyCountry !== 'US')
		) {
			this.menuItems.push({
				type: MenuItemType.learning,
				title: $localize`:@@navComponent-learning:Learning`,
				link: '/learning',
				icon: 'sl-nav-learning',
			});
		}
	}

	async ngOnInit(): Promise<void> {
		this.scSubscription = this.scService.systemCapabilitiesUpdated.subscribe(
			() => {
				this.refreshCapabilityDependentState();
			}
		);

		this.userProfile = await this.profileService.getUserProfile();
		this.isSentrilock =
			this.userProfile.activeRoleDetails?.company?.isSentrilock;
		this.activeCompanyCountry =
			this.userProfile.activeRoleDetails?.company?.address.countryCode;
		this.refreshCapabilityDependentState();

		// Use NavStateService to persist flag for app session only
		// because of re-initializing the nav component on link change
		if (this.isMobile && !this.navState.hasShownMobileSidenav) {
			this.sidenavOpened = true;
			this.navState.hasShownMobileSidenav = true;
		} else if (this.isMobile) {
			// If the user is on mobile and the sidenav is already open, close it
			this.sidenavOpened = false;
		}

		this.breakpointSub = this.breakpointObserver
			.observe([`(max-width: ${MOBILE_BREAKPOINT}px)`])
			.subscribe((state: BreakpointState) => {
				this.checkScreenSize(state.matches);
			});
	}

	async ngAfterViewInit(): Promise<void> {
		this.profileSubscription = this.profileService.profileUpdated.subscribe(
			async () => {
				this.userProfile = await this.profileService.getUserProfile(); // Update the profile when it changes
				this.isSentrilock =
					this.userProfile.activeRoleDetails?.company?.isSentrilock;
				this.activeCompanyCountry =
					this.userProfile.activeRoleDetails?.company?.address.countryCode;
				this.refreshCapabilityDependentState();
			}
		);
		this.navigationSubscription =
			this.navHistory.navigationItemChangeOccurred.subscribe(
				(item: MenuItemType) => {
					this.lastSelectedNavItem = item;
				}
			);
		this.lastSelectedNavItem = this.navHistory.getLastNavigationItemClicked();
	}

	navItemClicked(item: MenuItem): void {
		// Set navigation flag to prevent responsive behavior
		this.isNavigating = true;

		// Close sidenav on mobile
		if (this.isMobile) {
			this.sidenavOpened = false;
		}

		this.navHistory.setLastNavigationItemClicked(item.type);
		this.lastSelectedNavItem = item.type;
		this.navHistory.clearHistory();

		// Reset navigation flag after a brief delay
		setTimeout(() => {
			this.isNavigating = false;
		}, 100);
	}

	toggleSidenav(): void {
		// Only allow toggling on mobile devices
		if (this.isMobile) {
			this.sidenavOpened = !this.sidenavOpened;
		}
	}

	ngOnDestroy(): void {
		this.profileSubscription?.unsubscribe();
		this.profileSubscription = undefined;
		this.navigationSubscription?.unsubscribe();
		this.navigationSubscription = undefined;
		this.scSubscription?.unsubscribe();
		this.scSubscription = undefined;
		this.breakpointSub?.unsubscribe();
		this.breakpointSub = undefined;
	}

	showProfilePopout(): void {
		// Don't open the dialog if no user profile data is available
		if (!this.userProfile) {
			return;
		}

		const rect = this.userNameRef.nativeElement.getBoundingClientRect();
		const position: DialogPosition = { top: `${rect.top}px`, left: '40px' };
		const minWidth = 400;
		const panelClass = 'user-profile-dialog';

		const config = { position, minWidth, panelClass, data: this.userProfile };
		const currentRoleId = this.assignedRole?.id;
		const openDialog = this.dialog.open(NavProfileInformationComponent, config);

		// No need to manually unsubscribe from the subscription because it will
		// be automatically unsubscribed when the dialog is closed
		openDialog.beforeClosed().subscribe((newRole: any) => {
			if (newRole && newRole?.id !== currentRoleId) {
				this.menuItems = [];
			}
		});
	}

	onGenerateCodeClick(): void {
		this.dialog.open(DeviceGenerateCodeDialogComponent, {
			panelClass: 'drawer-dialog',
			data: { deviceId: null, userProfile: this.userProfile, device: null },
		});
	}

	async logout(): Promise<void> {
		await this.authService.logout();
	}
}
